import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { usePatientProgress, getErrorLabel } from "../hooks/usePatientProgress";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../../shared/context/AuthContext';
import { selectPatientForUser } from '../../services/patientService';
import ProcessingErrorDialog from '../../shared/components/ProcessingErrorDialog';

/**
 * @param {{ title: string, onClick: () => void | Promise<void>, icon: import('react').ReactNode }} props
 */
function StatusIconButton({ title, onClick, icon }) {
    return (
        <Tooltip title={title}>
            <IconButton onClick={onClick} size="small">
                {icon}
            </IconButton>
        </Tooltip>
    );
}

/** @param {{ patientId: string }} props */
function OnlineDemoPatientProgressCell({ patientId }) {
    const navigate = useNavigate();
    const { auth } = useAuth();

    const handleRedirect = async () => {
        const user = auth.currentUser;
        if (!user) {
            navigate('/login', { replace: true });
            return;
        }
        try {
            await selectPatientForUser(user.uid, patientId);
            navigate('/dashboard', { replace: true, state: { forceOnline: true } });
        } catch (err) {
            console.error('failed to select patient', err);
        }
    };

    return (
        <StatusIconButton
            title="Click to view the models."
            onClick={handleRedirect}
            icon={<CheckCircleIcon sx={{ color: 'success.main' }} />}
        />
    );
}

/** @param {{ patientId: string }} props */
function NonDemoPatientProgressCell({ patientId }) {
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { auth } = useAuth();

    const {
        percentage,
        currentStepLabel,
        currentStepKey,
        isError,
        isStatusResolved,
        needsCorrection,
        isComplete,
        errorType
    } = usePatientProgress(patientId);

    const handleRedirect = async () => {
        const user = auth.currentUser;
        if (!user) {
            navigate('/login', { replace: true });
            return;
        }
        try {
            await selectPatientForUser(user.uid, patientId);
            navigate('/dashboard', { replace: true, state: { forceOnline: true } });
        } catch (err) {
            console.error('failed to select patient', err);
        }
    };

    if (isError) {
        return (
            <>
                <StatusIconButton
                    title="An error occurred. Click for details."
                    onClick={() => setErrorDialogOpen(true)}
                    icon={<ErrorIcon sx={{ color: 'error.light' }} />}
                />
                <ProcessingErrorDialog
                    open={errorDialogOpen}
                    onClose={() => setErrorDialogOpen(false)}
                    patientId={patientId}
                    stepLabel={getErrorLabel(currentStepKey, currentStepLabel)}
                    errorType={currentStepKey === 'upload_failed' ? 'upload' : errorType}
                />
            </>
        );
    }

    if (!isStatusResolved) {
        return (
            <Tooltip title={currentStepLabel || 'Loading patient status...'}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34 }}>
                    <CircularProgress size={20} thickness={5} />
                </Box>
            </Tooltip>
        );
    }

    if (isComplete) {
        return (
            <StatusIconButton
                title="Processing complete. Click to view the models."
                onClick={handleRedirect}
                icon={<CheckCircleIcon sx={{ color: 'success.main' }} />}
            />
        );
    }

    if (needsCorrection) {
        return (
            <StatusIconButton
                title="Corrections required. Click to review."
                onClick={handleRedirect}
                icon={<EditIcon sx={{ color: 'warning.main' }} />}
            />
        );
    }

    return (
        <Box sx={{ 
            width: '50%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            height: '100%',
            mt: 1
        }}>
            <Tooltip title={currentStepLabel || 'Starting...'}>
                <LinearProgress 
                    variant="determinate" 
                    value={percentage} 
                    sx={{ width: '100%', borderRadius: 5, height: 8 }} 
                />
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {percentage}%
            </Typography>
        </Box>
    );
}

/**
 * @param {{ patientId: string, isDemo?: boolean, isDemoOffline?: boolean }} props
 */
export default function PatientProgressCell({ patientId, isDemo = false, isDemoOffline = false }) {
    if (isDemo || isDemoOffline) {
        return <OnlineDemoPatientProgressCell patientId={patientId} />;
    }

    return <NonDemoPatientProgressCell patientId={patientId} />;
}